/** Image slots (A source / B reference) — slot display, file uploads, and the vision/bundle fetch triggered by picking an A image. Split from dialog-shell.js (1 file 1 duty). */

import { store } from '../../../core/store.js'
import { eventBus, EVENTS } from '../../../core/event-bus.js'
import { CONFIG } from '../../../core/config.js'
import { showToast } from '../../../utils/toast.js'
import { analyzeImageVision } from '../../../api/ai-tools.js'
import { fetchImageCards } from '../../../api/prompts.js'
import { detectAspectRatio } from '../../../utils/aspect-ratio.js'
import { uploadImage } from '../../../api/index.js'
import { useImageAsSource, useImageAsReference } from '../../create-ui/image-roles.js'
import { createLogger } from '../../../utils/logger.js'
const log = createLogger('create.dialog.image-slots')


// In-flight guard for the B-slot file upload (one reference upload at a time).
let _refUpload = false
// Monotonic vision-request token: a fast A→B source switch must not let A's
// late bundle/vision response overwrite B's cards. Only the latest request's
// result applies. why → source-read race (review F4).
let _visionSeq = 0
// Unsubscribe for the one-shot post-login cards retry (injected/anon source).
let _visionRetryOff = null
// URL of the external (injected, no slot ref) source whose cards were already
// requested — activateShell runs on every mode switch and must read them once.
let _externalCardsFor = null

/**
 * Decide whether an activateShell re-hydration must read cards for an
 * injected/external source (?injectImage — a URL with no typed slot ref that
 * never went through the IMAGE_SELECT pick path), and mark the URL as
 * requested so repeated mode switches don't re-read it.
 * @param {{url?: string, slotOrigin?: string}|null} ref - `sourceImage` state
 * @returns {boolean}
 */
export function externalCardsNeeded(ref) {
  const isExternal = !ref?.slotOrigin && /^https?:\/\//.test(ref?.url || '')
  const needs = isExternal && _externalCardsFor !== ref.url
  if (needs) _externalCardsFor = ref.url
  return needs
}

// ---- Source image (A) ----

function _getSourceEls() {
  return {
    icon: document.getElementById('dialogSourceIcon'),
    label: document.getElementById('dialogSourceLabel'),
    img: document.getElementById('dialogSourceImg'),
  }
}

/**
 * Show an image in the A (source) slot and sync `sourceImage` state; optionally
 * trigger the bundle-first vision read (the A image IS the prompt).
 * @param {string} url
 * @param {boolean} [triggerVision=true] - fetch this image's cards (bundle → vision fallback)
 * @param {string|null} [cloudflareId=null] - persisted image id for the bundle lookup
 */
export function showSourceImage(url, triggerVision = true, cloudflareId = null) {
  const el = _getSourceEls()
  if (!el.img) return
  el.img.src = url
  el.img.classList.remove('hidden')
  if (el.icon) el.icon.style.display = 'none'
  if (el.label) el.label.style.display = 'none'

  const _slotFor = (existing) =>
    existing.url === url && existing.slotOrigin
      ? { slotOrigin: existing.slotOrigin, slotId: existing.slotId }
      : { slotOrigin: undefined, slotId: undefined }

  const probe = new Image()
  probe.onload = () => {
    const detected = detectAspectRatio(probe.naturalWidth, probe.naturalHeight)
    const existing = store.getState('sourceImage') || {}
    store.setState('sourceImage', { ...existing, ..._slotFor(existing), url, detectedRatio: detected })
  }
  probe.onerror = () => {
    const existing = store.getState('sourceImage') || {}
    store.setState('sourceImage', { ...existing, ..._slotFor(existing), url })
  }
  probe.src = url

  if (triggerVision) loadSourceCards(url, cloudflareId)
}

/**
 * Load the A image's inspiration cards. A's pixels are the prompt → cards are
 * BUNDLE-FIRST: if this image has a persisted image_cards bundle, use it
 * (instant, curated, zero vision/fal cost — ADR-005 2.8.0 #2). ONLY when it has
 * no bundle do we fall back to live VISION (this image's own cards), never to
 * generic defaults. Both paths emit VISION_COMPLETE so the right-panel handler
 * renders the cards.
 * @param {string} url
 * @param {string|null} [cloudflareId=null] - persisted image id for the bundle lookup
 */
export function loadSourceCards(url, cloudflareId = null) {
  const seq = ++_visionSeq
  if (_visionRetryOff) { _visionRetryOff(); _visionRetryOff = null }
  store.setState('creation.visionAnalysis', null)
  store.setState('creation.visionLoading', true)
  fetchImageCards(cloudflareId || url)
    .then(bundle => {
      if (seq !== _visionSeq) return
      if (bundle?.has_cards && bundle.cards?.length) {
        store.setState('creation.visionLoading', false)
        eventBus.emit(EVENTS.VISION_COMPLETE, { cards: bundle.cards })
        return
      }
      return analyzeImageVision(url).then(result => {
        if (seq !== _visionSeq) return
        store.setState('creation.visionAnalysis', result)
        store.setState('creation.visionLoading', false)
        eventBus.emit(EVENTS.VISION_COMPLETE, result)
      })
    })
    .catch((err) => {
      if (seq !== _visionSeq) return
      store.setState('creation.visionLoading', false)
      // Anonymous injected image: bundle/vision need a customer token. Skip
      // quietly and retry ONCE after login so the cards appear post-sign-in
      // (storefront funnel — ADR-005 2.7.0), instead of an error at the door.
      // Branch on err.status/err.code (apiRequest normalizes the platform
      // envelope) — prose-matching broke when errors became {code, message}.
      if (err?.status === 401 || err?.code === 'UNAUTHORIZED' || /401|unauthori[sz]ed/i.test(err?.message || '')) {
        _visionRetryOff = eventBus.once(EVENTS.USER_LOGIN, () => {
          _visionRetryOff = null
          if (store.getState('sourceImage')?.url === url) loadSourceCards(url, cloudflareId)
        })
        return
      }
      // Non-blocking: the user can still generate; only the auto-cards are
      // missing. Un-stick the once-per-URL guard so a later create activation
      // retries instead of failing permanently.
      _externalCardsFor = null
      log.warn('source image read failed:', err)
      showToast("Couldn't read this image", 'info', 2500)
    })
}

/**
 * Clear the A slot back to its placeholder and null the related state.
 */
export function clearSourceImage() {
  const el = _getSourceEls()
  if (el.img) { el.img.src = ''; el.img.classList.add('hidden') }
  if (el.icon) el.icon.style.display = ''
  if (el.label) el.label.style.display = ''
  store.setState('sourceImage', null)
  store.setState('creation.visionAnalysis', null)
}

// ---- Reference image (B) ----

/**
 * Upload a file dropped/picked into the B (reference) slot: instant data-URL
 * preview, then background upload for a typed asset ref.
 * @param {File} file
 */
export async function handleReferenceUpload(file) {
  if (!CONFIG.ALLOWED_TYPES.includes(file.type)) { showToast('Unsupported file type', 'error'); return }
  if (file.size > CONFIG.MAX_FILE_SIZE) { showToast('File too large (max 50MB)', 'error'); return }
  if (_refUpload) return
  _refUpload = true
  const reader = new FileReader()
  reader.onload = (e) => showReferenceImage(e.target.result)
  reader.readAsDataURL(file)
  try {
    const fd = new FormData(); fd.append('image', file); fd.append('source', 'reference')
    const asset = await uploadImage(fd)
    const url = asset?.public_url || asset?.url || null
    store.setState('creation.secondImageUrl', url)
    store.setState('creation.referenceAssetId', asset?.id || null)
    if (url) {
      showReferenceImage(url)
      const intent = store.getState('creation.intentCard')
      // Single-image intent: a reference is not a 2nd pixel — vision it into cards.
      if (!intent?.requires_second_image) useImageAsReference({ url, id: asset?.id }, 'asset')
    }
  } catch (err) {
    log.error('reference upload failed:', err)
    showToast('Reference upload failed', 'error')
    // The instant FileReader preview is already mounted but no reference state
    // exists — clear it so the B slot doesn't show a ghost image that generate
    // will never use. why → review F9.
    clearReferenceImage()
  } finally {
    _refUpload = false
  }
}

/**
 * Clear the B (reference) slot back to its placeholder.
 */
function clearReferenceImage() {
  const img = document.getElementById('dialogReferenceImg')
  const icon = document.getElementById('dialogReferenceIcon')
  const label = document.getElementById('dialogReferenceLabel')
  if (img) { img.src = ''; img.classList.add('hidden') }
  if (icon) icon.style.display = ''
  if (label) label.style.display = ''
}

/**
 * Show an image in the B (reference) slot.
 * @param {string} url
 */
export function showReferenceImage(url) {
  const img = document.getElementById('dialogReferenceImg')
  const icon = document.getElementById('dialogReferenceIcon')
  const label = document.getElementById('dialogReferenceLabel')
  if (!img) return
  img.src = url; img.classList.remove('hidden')
  if (icon) icon.style.display = 'none'
  if (label) label.style.display = 'none'
}

// ---- Image upload (A source) ----

/**
 * Upload a file dropped/picked into the A (source) slot: instant data-URL
 * preview, background upload to a typed asset ref (SSRF gate rejects data-URLs).
 * @param {File} file
 */
export function handleImageUpload(file) {
  if (!CONFIG.ALLOWED_TYPES.includes(file.type)) { showToast('Unsupported file type — use JPEG / PNG / WebP / GIF', 'error'); return }
  if (file.size > CONFIG.MAX_FILE_SIZE) { showToast('File too large (max 50MB)', 'error'); return }
  // Instant preview; background upload for a typed asset ref so generate's SSRF gate
  // accepts it (a data-URL source 422s). Mirrors the gallery pick path.
  const reader = new FileReader()
  reader.onload = (e) => showSourceImage(e.target.result, false)
  reader.readAsDataURL(file)
  const fd = new FormData(); fd.append('image', file); fd.append('source', 'source')
  uploadImage(fd)
    .then(asset => { const url = asset?.public_url || asset?.url; if (url && asset?.id != null) useImageAsSource({ url, id: asset.id }, 'asset', { silent: true }) })
    .catch(err => { log.error('[create] source upload failed:', err); showToast('Upload failed — pick from your library instead', 'error') })
}
